<script setup lang="ts">

import { Assets } from '../fin/assets';
import NumericInput from './NumericInput.vue';
import Percentage from './Percentage.vue';
import Validation from './Validation.vue';
import { AssetTypeId } from '../fin/assets';
import { StaticMarketData } from '../fin/market';
import { NormalDist } from '../fin/normal';
import { Direction } from '../fin/optimisation';
import { Person } from '../fin/person';
import { RetirementConfig } from '../fin/retirement_config';
import { RetirementStrategy } from '../fin/retirement_strategy';
import { makeSimpleNumericBoundsValidator } from './inputValidation';
import { onMounted, ref, Ref } from 'vue';

interface Props {
  showMarketUncertainty: boolean;
  earlyPensionWithdrawalGuidance: Direction;
  postRetirementIsaSavingGuidance: Direction;
};

const {
  showMarketUncertainty = false,
  earlyPensionWithdrawalGuidance = Direction.NO_GUIDANCE,
  postRetirementIsaSavingGuidance = Direction.NO_GUIDANCE,
} = defineProps<Props>();

function arrowHtmlCell(d: Direction): string {
  if (d == Direction.LARGER) {
    return `
      <svg width="65" height="65"
           version="1.1" xmlns="http://www.w3.org/2000/svg">
        <polygon transform="translate(0 50) scale(0.1) rotate(270)"
                 class="fin-optimize-arrow"
                 points="0 100 150 100 150 0 325 150 150 300 150 200 0 200"
                 stroke="limegreen" fill="lime" stroke-width="2">
          <title>Increasing this value is likely to extend your retirement at 
                 the desired net income level.</title>
        </polygon>
      </svg>
    `;
  } else if (d == Direction.SMALLER) {
    return `
      <svg width="65" height="65"
           version="1.1" xmlns="http://www.w3.org/2000/svg">
        <polygon transform="translate(30 20) scale(0.1) rotate(90)"
                 class="fin-optimize-arrow"
                 points="0 100 150 100 150 0 325 150 150 300 150 200 0 200"
                 stroke="red" fill="red" stroke-width="2">
          <title>Decreasing this value is likely to extend your retirement at
                 the desired net income level.</title>
        </polygon>
      </svg>
    `;
  } else {
    return '';
  }
}

const emit = defineEmits(['configChanged']);

const birth_date = ref<string>('1970-01-01');

const desired_net_income = ref<number>(0);
const retirement_age_years = ref<number>(0);
const max_age_years = ref<number>(0);
const start_year = ref<number>(0);

const initial_pension = ref<number>(0);
const initial_isa = ref<number>(0);
const initial_unprotected = ref<number>(0);
const final_salary_pension_start_age_years = ref<number>(0);
const final_salary_pension_pa = ref<number>(0);

const pension_withdrawal_age_years = ref<number>(0);
const state_pension_age_years = ref<number>(0);
const taxed_growth_rate = ref<number>(0);
const taxed_growth_rate_stddev = ref<number>(0);
const untaxed_growth_rate = ref<number>(0);
const untaxed_growth_rate_stddev = ref<number>(0);
const state_pension_pa = ref<number>(0);
const tax_free_pension_allowance = ref<number>(0);

const desired_pre_retirement_pension_savings = ref<number>(0);
const desired_pre_retirement_unprotected_savings = ref<number>(0);
const desired_pre_retirement_isa_savings = ref<number>(0);
const desired_post_retirement_isa_savings = ref<number>(0);
const early_pension_withdrawal_pa = ref<number>(0); 

const config = ref<RetirementConfig>(getConfig());

function fetchNumber(
  key: string,
  model: Ref<number>,
  default_value: number,
){
  const value = localStorage.getItem(key);
  if (value == null) {
    model.value = default_value;
  } else {
    const numberValue: number = +value;
    model.value = numberValue;
  }
}

function fetchDateString(
  key: string,
  model: Ref<string>,
  default_value: string,
){
  const value = localStorage.getItem(key);
  if (value == null) {
    model.value = default_value;
  } else {
    const ts = Date.parse(value);
    const d = new Date(ts);
    const s = d.getUTCFullYear()
            + '-'
            + String(d.getUTCMonth()+1).padStart(2, '0')
            + '-'
            + String(d.getUTCDate()).padStart(2, '0');
    model.value = s;
  }
}

function storeNumber(
  key: string,
  model: Ref<number>,
){
  const value = model.value.toString();
  localStorage.setItem(key, value);
}

function storeDateString(
  key: string,
  model: Ref<string>,
){
  const value = model.value;
  localStorage.setItem(key, value);
}

function load() {
  fetchDateString('birth_date', birth_date, '1970-01-01');

  fetchNumber('desired_net_income', desired_net_income, 30_000);
  fetchNumber('retirement_age_years', retirement_age_years, 67);
  fetchNumber('max_age_years', max_age_years, 125);
  fetchNumber('start_year', start_year, new Date().getUTCFullYear());

  fetchNumber(
    'pension_withdrawal_age_years', pension_withdrawal_age_years, 57);

  fetchNumber('state_pension_age_years', state_pension_age_years, 67);

  // VERY CONSERVATIVE DEFAULT
  fetchNumber('taxed_growth_rate', taxed_growth_rate, 0.01);
  fetchNumber('taxed_growth_rate_stddev', taxed_growth_rate_stddev, 0.0);
  fetchNumber('untaxed_growth_rate', untaxed_growth_rate, 0.015);
  fetchNumber('untaxed_growth_rate_stddev', untaxed_growth_rate_stddev, 0.0);

  fetchNumber('state_pension_pa', state_pension_pa, 11_502);

  fetchNumber('tax_free_pension_allowance', tax_free_pension_allowance, 268_275);

  fetchNumber(
    'desired_pre_retirement_pension_savings',
    desired_pre_retirement_pension_savings,
    5000);

  fetchNumber(
    'desired_pre_retirement_unprotected_savings',
    desired_pre_retirement_unprotected_savings,
    5000);

  fetchNumber(
    'desired_pre_retirement_isa_savings',
    desired_pre_retirement_isa_savings,
    20000);

  fetchNumber(
    'desired_post_retirement_isa_savings',
    desired_post_retirement_isa_savings,
    20000);

  fetchNumber('early_pension_withdrawal_pa', early_pension_withdrawal_pa, 0);

  fetchNumber('initial_pension', initial_pension, 0);
  fetchNumber('initial_isa', initial_isa, 0);
  fetchNumber('initial_unprotected', initial_unprotected, 0);

  fetchNumber(
    'final_salary_pension_start_age_years',
    final_salary_pension_start_age_years,
    0);

  fetchNumber('final_salary_pension_pa', final_salary_pension_pa, 0);
}

function save() {
  storeDateString('birth_date', birth_date);

  storeNumber('desired_net_income', desired_net_income);
  storeNumber('retirement_age_years', retirement_age_years);
  storeNumber('max_age_years', max_age_years);
  storeNumber('start_year', start_year);
  
  storeNumber('initial_unprotected', initial_unprotected);
  storeNumber('pension_withdrawal_age_years', pension_withdrawal_age_years);
  storeNumber('state_pension_age_years', state_pension_age_years);

  storeNumber('taxed_growth_rate', taxed_growth_rate);
  storeNumber('taxed_growth_rate_stddev', taxed_growth_rate_stddev);
  storeNumber('untaxed_growth_rate', untaxed_growth_rate);
  storeNumber('untaxed_growth_rate_stddev', untaxed_growth_rate_stddev);

  storeNumber('state_pension_pa', state_pension_pa)
  storeNumber('tax_free_pension_allowance', tax_free_pension_allowance);

  storeNumber(
    'desired_pre_retirement_pension_savings',
    desired_pre_retirement_pension_savings);

  storeNumber(
    'desired_pre_retirement_isa_savings',
    desired_pre_retirement_isa_savings);

  storeNumber(
    'desired_pre_retirement_unprotected_savings',
    desired_pre_retirement_unprotected_savings);

  storeNumber(
    'desired_post_retirement_isa_savings',
    desired_post_retirement_isa_savings);

  storeNumber('early_pension_withdrawal_pa', early_pension_withdrawal_pa);

  storeNumber('initial_pension', initial_pension);
  storeNumber('initial_isa', initial_isa);

  storeNumber(
    'final_salary_pension_start_age_years',
    final_salary_pension_start_age_years);

  storeNumber('final_salary_pension_pa', final_salary_pension_pa);
}

function getPreRetirementSavingRatesPa(): Assets {
  return new Assets()
    .credit(AssetTypeId.Pension, desired_pre_retirement_pension_savings.value)
    .credit(
      AssetTypeId.Unprotected,
      desired_pre_retirement_unprotected_savings.value)
    .credit(AssetTypeId.ISA, desired_pre_retirement_isa_savings.value);
}

function getStrategy(): RetirementStrategy {
  return new RetirementStrategy(
    getPreRetirementSavingRatesPa(),
    desired_post_retirement_isa_savings.value,
    desired_net_income.value,
    early_pension_withdrawal_pa.value,
    max_age_years.value,
  );
}

function getMarket(): StaticMarketData {
  return new StaticMarketData(
    pension_withdrawal_age_years.value,
    state_pension_age_years.value,
    new NormalDist(
      taxed_growth_rate.value,
      showMarketUncertainty ? taxed_growth_rate_stddev.value : 0),
    new NormalDist(
      untaxed_growth_rate.value,
      showMarketUncertainty ? untaxed_growth_rate_stddev.value : 0),
    state_pension_pa.value,
  );
}

function getPerson(): Person{
  const bd = new Date(Date.parse(birth_date.value));
  return new Person(
    'Someone',
    bd, // birth date
    retirement_age_years.value as number, // retirement age
    max_age_years.value, // max age

    // private pension age: when you can start drawing any pension
    pension_withdrawal_age_years.value,

    // age when any final salary pension kicks in
    final_salary_pension_start_age_years.value,

    // final salary pension payment (pre-tax) per year
    final_salary_pension_pa.value,
    
    // simulation start year
    start_year.value,
  );
}

function getInitialAssets(): Assets {
  return new Assets()
    .credit(AssetTypeId.Pension, initial_pension.value as number)
    .credit(AssetTypeId.ISA, initial_isa.value as number)
    .credit(AssetTypeId.Unprotected, initial_unprotected.value as number)
    .credit(AssetTypeId.TaxFreePensionAllowance, tax_free_pension_allowance.value as number);
}

function getConfig(): RetirementConfig {
  return new RetirementConfig(
    getPerson(),
    getInitialAssets(),
    getMarket(),
    getStrategy(),
  );
}

function publish() {
  config.value = getConfig();
  emit('configChanged', config.value);
}

function loadAndPublish() {
  load();
  publish();
}

function saveAndPublish() {
  save();
  publish();
}

function duplicateHoverTextFromInputToAnnotation() {
  const sources = Array.from(document.getElementsByTagName('input')).filter(
    elem => (
      ['number', 'text'].includes(
        elem.getAttribute('type') ?? 'NEVER'
      ) && elem.getAttribute('title')
    )
  );

  for (const elem of sources) {
    const text = elem.getAttribute('title')!;
    const annotation = elem.parentElement?.previousElementSibling;
    annotation?.setAttribute('title', text);
  }
};

onMounted( () => {
  duplicateHoverTextFromInputToAnnotation();
  loadAndPublish();
});

defineExpose({load});

</script>

<template>
  <div class="flex-container">
    <div class="input-card" id="person-input-card">
      <h3 title="Things about you.">Personal Details</h3>
      <table><tbody>
        <tr>
          <td span
            title="When where you born? Needed because various benefits and taxes are age dependent.">
            birth date
          </td><td>
            <input id="fin-input-value-birthDate"
                   type="date"
                   v-model="birth_date"
                   @change="saveAndPublish"
                   class="date-picker" />
          </td>
        </tr>
        <tr>
          <td>
            desired net income (GBP/y)
          </td>
          <td>
            <NumericInput
              id="fin-input-value-desiredNetIncome"
              title="How much income do you want per year (in today's money: allowing for inflation), after taxes?"
              @valueChange="saveAndPublish"
              v-model="desired_net_income"
              arrow-navigation="true"
              :validator="makeSimpleNumericBoundsValidator( 'income', 0, 1_000_000)" />
          </td>
        </tr><tr>
          <td>
            retirement age (y)
          </td>
          <td>
            <NumericInput
              id="fin-input-value-retirementAge"
              title="When you stop working/earning, not start of state pension. Can be before you're allowed to withdraw from your pension assets without penalty, if you have enough unprotected/ISA assets."
              @valueChange="saveAndPublish"
              v-model="retirement_age_years"
              :validator="makeSimpleNumericBoundsValidator('retirement age', 30, 100)"
            />
          </td>
        </tr><tr>
          <td>
            max age (y)
          </td>
          <td>
            <NumericInput
              id="fin-input-value-maxAge"
              title="How far in the future are you willing to let the simulation run?"
              @valueChange="saveAndPublish"
              v-model="max_age_years"
              :validator="makeSimpleNumericBoundsValidator('max age', 30, 150)"
            />
          </td>
        </tr><tr>
          <td>
            simulation start year 
          </td>
          <td>
            <NumericInput
              id="fin-input-value-startYear"
              title="When are we starting this simulation?"
              @valueChange="saveAndPublish"
              v-model="start_year"
              :validator="makeSimpleNumericBoundsValidator('simulation start year', 2020, 2050)"
            />
          </td>
        </tr>
      </tbody></table>
    </div>
    <div class="input-card" id="assets-input-card">
      <h3 title="How much you have saved for retirement so far. These pools will increase in size until retirement according to how fast you save and the growth rates specified in the Market section.">Initial Assets</h3>
      <table><tbody>
        <tr>
          <td>pension savings (GBP)</td>
          <td>
            <NumericInput
              id="fin-input-value-initialPension"
              title="Pension wrapped assets, not including final salary pensions"
              :validator="makeSimpleNumericBoundsValidator('pension savings', 0, 10_000_000)"
              @valueChange="saveAndPublish"
              v-model="initial_pension"
            />
          </td>
        </tr><tr>
          <td>ISA savings (GBP)</td>
          <td>
            <NumericInput
              id="fin-input-value-initialIsa"
              title="ISA (individual savings account), wrapped assets"
              :validator="makeSimpleNumericBoundsValidator('ISA savings', 0, 10_000_000)"
              @valueChange="saveAndPublish"
              v-model="initial_isa"
            />
          </td>
        </tr><tr>
          <td>unprotected savings (GBP)</td>
          <td>
            <NumericInput
              id="fin-input-value-initialUnprotected"
              title="Assets such as equities that attract dividend and capital gains taxes"
              :validator="makeSimpleNumericBoundsValidator('unprotected savings', 0, 10_000_000)"
              @valueChange="saveAndPublish"
              v-model="initial_unprotected" />
          </td>
        </tr><tr>
          <td>defined benefit pension start age (y)</td>
          <td>
            <NumericInput
              id="fin-input-value-finalSalaryPensionStartAge"
              title="Your age in years when any defined benefit pension payments start"
              :validator="makeSimpleNumericBoundsValidator('defined benefit start age', 50, 75)"
              @valueChange="saveAndPublish"
              v-model="final_salary_pension_start_age_years"
            />
          </td>
        </tr><tr>
          <td>defined benefit pension amount (GBP/y)</td>
          <td>
            <NumericInput
              id="fin-input-value-finalSalaryPensionPerYear"
              title="Your defined benefit pension annual income. If you don't have any defined benefit pension savings, then set this to zero."
              :validator="makeSimpleNumericBoundsValidator('defined benefit annual payment amount', 0, 1_000_000)"
              @valueChange="saveAndPublish"
              v-model="final_salary_pension_pa"
            />
          </td>
        </tr>
      </tbody></table>
    </div>
    <div class="input-card" id="market-input-card">
      <h3 title="Typically you cannot control the values in this section: they are set by government rules and market behaviour. Different users views of likely future asset growth rates are unknown - you have to choose how optimistic you want to be, using historic rates as a guide.">Market Details</h3>
      <table><tbody>
        <tr>
          <td>
            minimum pension withdrawal age (y)
          </td><td>
            <NumericInput
              id="fin-input-value-pensionAvailableStartAge"
              title="pension withdrawal age: when you are allowed to start drawing from your pension assets without penalty"
              :validator="makeSimpleNumericBoundsValidator('pension withdrawal age', 50, 75)"
              @valueChange="saveAndPublish"
              v-model="pension_withdrawal_age_years"
            />
          </td>
        </tr><tr>
          <td>
            state pension age (y)
          </td>
          <td>
            <NumericInput
              id="fin-input-value-statePensionStartAge"
              title="when any state pension you have earned starts paying out"
              :validator="makeSimpleNumericBoundsValidator('state pension age', 50, 70)"
              @valueChange="saveAndPublish"
              v-model="state_pension_age_years"
            />
          </td>
        </tr><tr>
          <td>
            taxed real growth rate
          </td><td>
            <Percentage
              id="fin-input-value-taxedGrowthRate"
              title="Real (discounting inflation) growth rate of unprotected assets, net of taxation (dividend and capital gains). (0.01 means 1%)"
              @change="saveAndPublish"
              v-model="taxed_growth_rate"
              :validator="makeSimpleNumericBoundsValidator('real net-of-tax growth rate', 0, 0.5)"
            />
          </td>
        </tr>
        <tr>
          <td>
            untaxed real growth rate
          </td><td>
            <Percentage
              id="fin-input-value-unTaxedGrowthRate"
              title="Real (discounting inflation) growth rate of protected assets (ISA and pension). (0.01 means 1%)"
              @change="saveAndPublish"
              v-model="untaxed_growth_rate"
              :validator="makeSimpleNumericBoundsValidator('real protected growth rate', 0, 0.5)"
            />
          </td>
        </tr>
        <template v-if="showMarketUncertainty">
        <tr>
          <td>
            taxed growth rate uncertainty
          </td>
          <td>
            <Percentage
              id="fin-input-value-taxedGrowthRateStdDev"
              title="Standard deviation of taxed annual growth rate. Setting this to zero means the growth rate modelled will be exactly at the rate you set. The bigger you make this value, the more unpredictable the growth rate will be each year (including the possibility of negative growth), varying about the 'mean' value you have set above."
              @change="saveAndPublish"
              v-model="taxed_growth_rate_stddev"
              :validator="makeSimpleNumericBoundsValidator('real unprotected growth rate std dev', 0, 0.5)"
              class="uncertainty"
            />
          </td>
        </tr>
        <tr>
          <td>
            untaxed growth rate uncertainty
          </td>
          <td>
            <Percentage
              id="fin-input-value-unTaxedGrowthRateStdDev"
              title="Standard deviation of untaxed annual growth rate. Setting this to zero means the growth rate modelled will be exactly at the rate you set. The bigger you make this value, the more unpredictable the growth rate will be each year (including the possibility of negative growth), varying about the 'mean' value you have set above."
              @change="saveAndPublish"
              v-model="untaxed_growth_rate_stddev"
              :validator="makeSimpleNumericBoundsValidator('real protected growth rate std dev', 0, 0.5)"
              class="uncertainty"
            />
          </td>
        </tr>
        </template>
        <tr>
          <td>
            annual state pension amount (GBP)
          </td>
          <td>
            <NumericInput
              id="fin-input-value-statePensionPerYear"
              title="state pension per year (GBP)"
              :validator="makeSimpleNumericBoundsValidator('annual state pension amount', 0, 25_000)"
              @valueChange="saveAndPublish"
              v-model="state_pension_pa"
            />
          </td>
        </tr><tr>
          <td>
            tax free pension allowance (GBP)
          </td>
          <td>
            <NumericInput
              id="fin-input-value-taxFreePensionAllowance"
              title="tax free pension allowance (GBP)"
              :validator="makeSimpleNumericBoundsValidator('tax free pension allowance', 0, 10_000_000)"
              @valueChange="saveAndPublish"
              v-model="tax_free_pension_allowance"
            />
          </td>
        </tr>
      </tbody></table>
    </div>
    <div class="input-card" id="strategy-input-card">
      <h3 title="These are things you can (and probably should) control: How much to save, and where to save it before retirement. Then, during retirement you can adjust how your money flows to potentially improve tax efficiency.">Pre-Retirement Strategy</h3>
      <table><tbody>
        <tr>
          <td>
            desired pension saving (GBP/y)
          </td><td>
            <NumericInput
              id="fin-input-value-preRetirementPensionSaving"
              title="How much you plan to save in pension per year before retiring. This will increase your pension asset pool in addition to any natural growth."
              :validator="makeSimpleNumericBoundsValidator('desired pre-retirement pension saving', 0, 1000_000)"
              @valueChange="saveAndPublish"
              v-model="desired_pre_retirement_pension_savings"
            />
          </td>
        </tr><tr>
          <td>
            desired unprotected saving (GBP/y)
          </td><td>
            <NumericInput
              id="fin-input-value-preRetirementUnprotectedSaving"
              title="How much you plan to save in unprotected assets per year before retiring. This will increase your unprotected pool in addition to any natural growth."
              :validator="makeSimpleNumericBoundsValidator('desired pre-retirement unprotected saving', 0, 1_000_000)"
              @valueChange="saveAndPublish"
              v-model="desired_pre_retirement_unprotected_savings"
            />
          </td>
        </tr><tr>
          <td>
            desired ISA saving (GBP/y)
          </td><td>
            <NumericInput
              id="fin-input-value-preRetirementIsaSaving"
              title="How much you plan to save in ISA assets per year before retiring. This will increase your ISA pool in addition to any natural growth."
              :validator="makeSimpleNumericBoundsValidator('desired pre-retirement ISA saving', 0, 20_000)"
              @valueChange="saveAndPublish"
              v-model="desired_pre_retirement_isa_savings"
            />
          </td>
        </tr>
      </tbody></table>
      <hr title="Above this line, bigger numbers mean you save more and spend less before retirement. This simulation tool doesn't care how you got this money, it just keeps track of how much you save and how those savings pools grow.&#010Below the line, you are moving money around during retirement, which may affect tax efficiency."/>
      <h3>Post-Retirement Strategy</h3>
      <table> <tbody>
        <tr>
          <td>
            desired ISA saving (GBP/y)
          </td><td>
            <NumericInput
              id="fin-input-value-postRetirementIsaSaving"
              title="How much you plan to invest in ISA per year after retiring (until you've only got ISA assets left)?&#010Moving unprotected wealth to ISA may allow you to reduce the impact of income and capital gains tax on your retirement funds."
              :validator="makeSimpleNumericBoundsValidator('desired post-retirement ISA saving', 0, 20_000)"
              @valueChange="saveAndPublish"
              v-model="desired_post_retirement_isa_savings"
            />
          </td>
          <td v-html="arrowHtmlCell(postRetirementIsaSavingGuidance)"/>
        </tr><tr>
          <td>
            early pension withdrawal rate (GBP/y)
          </td><td>
            <NumericInput
              id="fin-input-value-earlyPensionWithdrawalRate"
              title="Starting to draw some pension before you've run out of unprotected assets could be beneficial depending on income tax thresholds: you might lose more to capital gains and dividend taxation but might save on lifetime income tax.&#010If this value is set too high, so that your net income in combination with other fixed income streams (such as final salary pensions and state pensions) is greater than you have configured, the tool will not consume as much pension as you have specified here."
              :validator="makeSimpleNumericBoundsValidator('desired post-retirement ISA saving', 0, 1_000_000)"
              @valueChange="saveAndPublish"
              v-model="early_pension_withdrawal_pa"
            />
          </td>
          <td v-html="arrowHtmlCell(earlyPensionWithdrawalGuidance)"/>
        </tr>
        </tbody></table>
    </div>
  </div>
  <Validation :config="config"/>
</template>

<style scoped>

.input-card {
  border-color: skyblue;
}

input {
  width: 110px;
}

</style>
